import {
  InviteResponseSchema,
  PresenceResponseSchema,
  type InviteResponse,
  type PresenceResponse,
} from '@il-colpo/protocol';
import { getAccountStore } from '../account/accountStore.ts';
import { gameServerUrl } from '../online/config.ts';

/**
 * The social calls of the game server (`apps/server/src/social-http.ts`), made with the player's
 * own access token: who of the friends is here, and an invitation to the private room the player is
 * in. A call that cannot be made (no server, no token, the network) answers `null`: the app goes on
 * without presence, as it does without the cloud.
 */

/** The HTTP address of the game server (the same host as its WebSocket one). */
export function gameServerHttpUrl(url: string | null = gameServerUrl()): string | null {
  if (!url) return null;
  return url.replace(/^ws(s?):\/\//, 'http$1://').replace(/\/+$/, '');
}

async function call(path: string, init: RequestInit): Promise<Response | null> {
  const base = gameServerHttpUrl();
  if (!base) return null;
  const client = await getAccountStore().getState().ensure();
  const token = client ? await client.accessToken() : null;
  if (!token) return null;
  try {
    return await fetch(`${base}${path}`, {
      ...init,
      headers: { ...init.headers, authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(8_000),
    });
  } catch {
    return null;
  }
}

/** Says the player is here, and where the friends in `ids` are, and who has invited the player. */
export async function fetchPresence(ids: readonly string[]): Promise<PresenceResponse | null> {
  const response = await call(`/social/presence?ids=${encodeURIComponent(ids.join(','))}`, {
    method: 'GET',
  });
  if (!response?.ok) return null;
  try {
    const parsed = PresenceResponseSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Invites a friend to the private room the player is in. */
export async function sendInvite(friendId: string): Promise<InviteResponse | null> {
  const response = await call(`/social/invite/${encodeURIComponent(friendId)}`, { method: 'POST' });
  if (!response) return null;
  try {
    const parsed = InviteResponseSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
